import { DatePicker, Descriptions } from 'antd';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useNotification from '../../../hooks/useNotification';
import { IBannerDetail } from '../../../models/banner.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import Radio from '../../Radio';
import Checkbox from '../../Checkbox';
import CustomTimePicker from '../../CustomTimePicker';
import FileUpload from '../../FileUpload';
import ConfirmModal from '../../modals/ConfirmModal';
import SelectCampusModal from '../../modals/SelectCampusModal';
import SelectCampus from '../../SelectCampus';
import { YN } from '../../../models/common.api.model';
import useAuth from '../../../hooks/useAuth';
import MultiCampusModal from '../../statistics/regular-test/modals/MultiCampusModal';

export interface IProps {
  data?: IBannerDetail;
  remove?: () => void;
  submit: (data: FormData) => void;
}

const TopBannerEditor: FC<IProps> = ({ data, remove, submit }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { infoNotification } = useNotification();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);

  const [title, setTitle] = useState('');
  const [bannerGBN, setBannerGBN] = useState('TO');
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [srtDate, setSrtDate] = useState('');
  const [srtTime, setSrtTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isPcOpen, setIsPcOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(true);
  const [pcFile, setPcFile] = useState('');
  const [mobileFile, setMobileFile] = useState('');
  const [pcFileCode, setPcFileCode] = useState<number | undefined>();
  const [mobileFileCode, setMobileFileCode] = useState<number | undefined>();
  const [openYN, setOpenYN] = useState<YN>('N');
  const [seq, setSeq] = useState('');
  const [url, setUrl] = useState<string | undefined>('');
  const [memo, setMemo] = useState<string | undefined>('');
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [mode, setMode] = useState('create');
  const [confirmRemoveVisible, setConfirmRemoveVisible] = useState(false);

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setBannerGBN(data.bannerGBN);
      setCampusCodes(data.campuses.map((item) => item.campusCode));
      setSrtDate(data.srtDate);
      setSrtTime(data.srtTime);
      setEndDate(data.endDate);
      setEndTime(data.endTime);
      if (data.pcAttachFile) {
        setIsPcOpen(true);
        setPcFileCode(data.pcAttachFile.attachFileCode);
      } else {
        setIsPcOpen(false);
      }
      if (data.moAttachFile) {
        setIsMobileOpen(true);
        setMobileFileCode(data.moAttachFile.attachFileCode);
      } else {
        setIsMobileOpen(false);
      }
      setOpenYN(data.openYN);
      setSeq(String(data.seq));
      setUrl(data.url);
      setMemo(data.memo);
      setMode('read');
    } else {
      setSrtDate(moment().format('YYYYMMDD'));
      setSrtTime('00:00');
      setEndDate(moment().add(1, 'days').format('YYYYMMDD'));
      setEndTime('00:00');
    }
  }, [data]);

  useEffect(() => {
    if (auth?.hqYn === 'N' && currentCampus) {
      setCampusCodes([currentCampus]);
    }
  }, [auth, currentCampus]);

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!srtDate || !srtTime) {
      return infoNotification(t('homepage.mainBanner.message1'));
    }
    if (!endDate || !endTime) {
      return infoNotification(t('homepage.mainBanner.message2'));
    }
    if (!isPcOpen && !isMobileOpen) {
      return infoNotification(t('homepage.topBanner.message1'));
    }
    if (isPcOpen && !(pcFile || pcFileCode)) {
      return infoNotification(t('homepage.topBanner.message2'));
    }
    if (isMobileOpen && !(mobileFile || mobileFileCode)) {
      return infoNotification(t('homepage.topBanner.message3'));
    }
    if (!seq) {
      return infoNotification(t('homepage.topBanner.message4'));
    }

    const form = new FormData();
    form.append('bannerGBN', bannerGBN);
    form.append('title', title);
    form.append('srtDate', srtDate);
    form.append('srtTime', srtTime);
    form.append('endDate', endDate);
    form.append('endTime', endTime);
    form.append('openYN', openYN);
    form.append('seq', seq);
    form.append('bannerType', 'IM');

    if (campusCodes.length) {
      form.append('allCampusYN', 'N');
      campusCodes.forEach((code) => form.append('campusCodes', code));
    } else {
      form.append('allCampusYN', 'Y');
    }
    if (pcFile) {
      form.append('pcAttachFile', pcFile);
    }
    if (mobileFile) {
      form.append('moAttachFile', mobileFile);
    }
    if (pcFileCode) {
      form.append('pcAttachFileCode', String(pcFileCode));
    }
    if (mobileFileCode) {
      form.append('moAttachFileCode', String(mobileFileCode));
    }
    if (mode === 'edit') {
      // PC용 첨부파일
      if (pcFile && pcFileCode) {
        form.append('pcAttachFileStatus', 'update');
      } else if (pcFile && !pcFileCode) {
        form.append('pcAttachFileStatus', 'create');
      } else if (pcFileCode && !isPcOpen) {
        form.append('pcAttachFileStatus', 'delete');
      }
      // 모바일용 첨부파일
      if (mobileFile && mobileFileCode) {
        form.append('moAttachFileStatus', 'update');
      } else if (mobileFile && !mobileFileCode) {
        form.append('mobileAttachFileStatus', 'create');
      } else if (mobileFileCode && !isMobileOpen) {
        form.append('moAttachFileStatus', 'delete');
      }
    }
    if (url) {
      form.append('url', url);
    }
    if (memo) {
      form.append('memo', memo);
    }

    submit(form);
  };

  return (
    <div className='comm-table__area rowType'>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={2}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.topBanner.column1')}`}>
          <Radio
            checked={bannerGBN === 'TO'}
            onChange={() => setBannerGBN('TO')}
            text={t('homepage.topBanner.option1')}
            disabled={mode === 'read'}
          />
          <Radio
            className="ml-4"
            checked={bannerGBN === 'FR'}
            onChange={() => setBannerGBN('FR')}
            text={t('homepage.topBanner.option2')}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column11')}>
          {auth.hqYn === 'Y' ? (
            <div className="flex">
              <div className='comm-btn__area'>
                <Button
                  className="btn-basic mint"
                  onClick={() => setMultiCampusVisible(true)}
                  text={t('event.education.button2')}
                  disabled={bannerGBN === 'FR' || mode === 'read'}
                />
              </div>
              <div className="v-line ml-2">
                {campusCodes.length
                  ? `(${campusCodes.length}${t('event.education.text8')})`
                  : `(${t('event.education.text9')})`}
              </div>
            </div>
          ) : (
            <SelectCampus />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column2')}`}>
          <div className='comm-gap'>
            <div className="date-picker__area">
              <DatePicker
                className="mr-1"
                value={srtDate ? moment(srtDate) : undefined}
                onChange={(e) => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'));
                }}
                disabled={mode === 'read'}
              />
            </div>
            <div className="time-picker__area">
              <CustomTimePicker
                value={srtTime}
                onSelect={(value) => setSrtTime(moment(value).format('HH:mm'))}
                disabled={mode === 'read'}
              />
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column3')}`}>
          <div className='comm-gap'>
            <div className="date-picker__area">
              <DatePicker
                className="mr-1"
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'));
                }}
                disabled={mode === 'read'}
              />
            </div>
            <div className="time-picker__area">
              <CustomTimePicker
                value={endTime}
                onSelect={(value) => setEndTime(moment(value).format('HH:mm'))}
                disabled={mode === 'read'}
              />
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.topBanner.column2')}`} span={2}>
          <div className="flex">
            <Checkbox
              checked={isPcOpen}
              onChange={() => setIsPcOpen(!isPcOpen)}
              text="PC"
              disabled={mode === 'read'}
            />
            <div className="ml-4" />
            <Checkbox
              checked={isMobileOpen}
              onChange={() => setIsMobileOpen(!isMobileOpen)}
              text={t('homepage.topBanner.option3')}
              disabled={mode === 'read'}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column5')}>
          {data?.pcAttachFile ? (
            <>
              <a
                className="download-link btn-text"
                href={data.pcAttachFile.fileLocation}
                download={data.pcAttachFile.originalName}
              >
                {data.pcAttachFile.originalName}
              </a>
              <br />
            </>
          ) : null}
          {mode !== 'read' && (
            <FileUpload
              file={pcFile}
              setFile={setPcFile}
            />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column6')}>
          {data?.moAttachFile ? (
            <>
              <a
                className="download-link btn-text"
                href={data.moAttachFile.fileLocation}
                download={data.moAttachFile.originalName}
              >
                {data.moAttachFile.originalName}
              </a>
              <br />
            </>
          ) : null}
          {mode !== 'read' && (
            <FileUpload
              file={mobileFile}
              setFile={setMobileFile}
            />            
          )}
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column7')}`} span={2}>
          <Radio
            checked={openYN === 'Y'}
            onChange={() => setOpenYN('Y')}
            text="Y"
            disabled={mode === 'read'}
          />
          <Radio
            className="ml-4"
            checked={openYN === 'N'}
            onChange={() => setOpenYN('N')}
            text="N"
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column9')}`} span={2}>
          <div className="flex">
            <input
              type="number"
              className="form-control w-100p"
              value={seq}
              onChange={(e) => setSeq(e.target.value)}
              disabled={mode === 'read'}
            />
            <span className="ml-2 v-line">
              {t('homepage.mainBanner.text1')} {`(${t('homepage.mainBanner.text2')})`}
            </span>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column10')} span={2}>
          <input
            type="text"
            className="form-control"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column11')} span={2}>
          <textarea
            rows={3}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push('/homepage/top-banner')}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          {mode === 'read' ? (
            <>
              <Button
                className="btn-basic gray"
                onClick={() => setConfirmRemoveVisible(true)}
                text={t('common.button.delete')}
                writeAuthYn={writeAuthYn}
              />
              <Button
                className="btn-basic pink-line"
                onClick={() => setMode('edit')}
                text={t('common.button.edit')}
                writeAuthYn={writeAuthYn}
              />
            </>
          ) : (
            <Button
              className="btn-basic pink"
              onClick={onSubmit}
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
            />
          )}
        </div>
      </div>

      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
      
      {confirmRemoveVisible && (
        <ConfirmModal
          visible={confirmRemoveVisible}
          setVisible={setConfirmRemoveVisible}
          onConfirm={() => {
            if (remove) remove();
          }}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
    </div>
  );
};

export default TopBannerEditor;
